import { Button, Container, Typography } from "@mui/material"
import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import {
  DataGridPro,
  GridColDef,
  GridColumnGroupingModel,
} from "@mui/x-data-grid-pro"
import { DateRange } from "@mui/x-date-pickers-pro"
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker"
import { dataGridDEde } from "constants/dataGridLocale"
import { Dayjs } from "dayjs"
import { EmployeeStats2Data } from "queries/fetchEmployeeStats2"
import { fetchEmployeeStats2WithStoredToken } from "queries/fetchEmployeeStats2"
import { useState } from "react"
import dayjs from "utils/dayjsConfig"
import { shortcutsItems } from "utils/shortcutItems"

// Helper function to format time from minutes to HH:MM
export const formatTime = (value: number): string => {
  const hours = Math.floor(value / 60)
    .toString()
    .padStart(2, "0")
  const mins = (value % 60).toString().padStart(2, "0")
  return `${hours}:${mins}`
}

// Helper function to format percentage
export const formatPct = (value: number): string => {
  if (value < 0 || value === undefined) {
    return "-"
  }
  return value.toFixed(2)
}

const EmployeeStats2Page: React.FC = () => {
  const [employeeStats, setEmployeeStats] = useState<EmployeeStats2Data[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([
    dayjs().subtract(7, "day"),
    dayjs(),
  ])

  const loadEmployeeStats = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      return
    }

    setIsLoading(true)
    try {
      const fromDate = dateRange[0]?.format("YYYY-MM-DD") || ""
      const toDate = dateRange[1]?.format("YYYY-MM-DD") || ""

      const response = await fetchEmployeeStats2WithStoredToken(
        fromDate,
        toDate
      )

      if (response?.stats) {
        setEmployeeStats(response.stats)
      } else {
        setEmployeeStats([])
      }
    } catch (error) {
      console.error("Error loading employee stats 2:", error)
      setEmployeeStats([])
    } finally {
      setIsLoading(false)
    }
  }

  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: "basic",
      headerName: "Grunddaten",
      description: "Grundlegende Mitarbeiterinformationen",
      children: [
        { field: "firstName" },
        { field: "lastName" },
        { field: "email" },
      ],
    },
    {
      groupId: "freshdesk",
      headerName: "Freshdesk",
      description: "Freshdesk Statistiken",
      children: [
        { field: "fdtickets" },
        { field: "fdactions" },
        { field: "fdemails" },
        { field: "fdchats" },
        { field: "fdnotes" },
        { field: "fdworked" },
        { field: "fdresolved" },
        { field: "fdclosed" },
      ],
    },
    {
      groupId: "ratings",
      headerName: "Bewertungen",
      description: "Kundenbewertungen",
      children: [
        { field: "fdokratings" },
        { field: "fdbadratings" },
        { field: "fdgoodratings" },
      ],
    },
    {
      groupId: "freshchat",
      headerName: "Freshchat",
      description: "Freshchat Statistiken",
      children: [
        { field: "fcassigned" },
        { field: "fcresolved" },
        { field: "fcuniqueResolved" },
        { field: "fcmessages" },
        { field: "fcresponses" },
        { field: "fcaverageRating" },
      ],
    },
  ]

  const columns: GridColDef<EmployeeStats2Data>[] = [
    {
      field: "firstName",
      headerName: "Vorname",
      width: 120,
      editable: false,
    },
    {
      field: "lastName",
      headerName: "Nachname",
      width: 120,
      editable: false,
    },
    {
      field: "email",
      headerName: "E-Mail",
      width: 200,
      editable: false,
    },
    {
      field: "adduration",
      headerName: "Arbeitsdauer",
      width: 120,
      editable: false,
      align: "right",
      headerAlign: "right",
      valueFormatter: (_, row) => formatTime(row.adduration),
    },
    {
      field: "fdtickets",
      headerName: "Tickets",
      width: 80,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fdactions",
      headerName: "Aktionen",
      width: 80,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fdemails",
      headerName: "E-Mails",
      width: 80,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fdchats",
      headerName: "Chats",
      width: 80,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fdnotes",
      headerName: "Notizen",
      width: 80,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fdokratings",
      headerName: "OK",
      width: 60,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fdbadratings",
      headerName: "Schlecht",
      width: 80,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fdgoodratings",
      headerName: "Gut",
      width: 60,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fdworked",
      headerName: "Bearbeitet",
      width: 100,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fdresolved",
      headerName: "Gelöst",
      width: 80,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fdclosed",
      headerName: "Geschlossen",
      width: 100,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fcassigned",
      headerName: "Zugewiesen",
      width: 100,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fcresolved",
      headerName: "Gelöst",
      width: 80,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fcuniqueResolved",
      headerName: "Einzigartig gelöst",
      width: 140,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fcmessages",
      headerName: "Nachrichten",
      width: 110,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fcresponses",
      headerName: "Antworten",
      width: 90,
      editable: false,
      align: "right",
      headerAlign: "right",
      type: "number",
    },
    {
      field: "fcaverageRating",
      headerName: "Ø Bewertung",
      width: 110,
      editable: false,
      align: "right",
      headerAlign: "right",
      valueFormatter: (_, row) => formatPct(row.fcaverageRating),
    },
  ]

  return (
    <Container maxWidth={false}>
      <Grid container spacing={3}>
        <Grid size={12}>
          <DateRangePicker
            localeText={{
              start: "Von",
              end: "Bis",
            }}
            value={dateRange}
            onChange={(newValue) => setDateRange(newValue)}
            slotProps={{
              shortcuts: {
                items: shortcutsItems,
              },
              actionBar: {
                actions: ["clear", "today"],
              },
            }}
          />
          <Button
            variant="contained"
            onClick={loadEmployeeStats}
            disabled={isLoading || !dateRange[0] || !dateRange[1]}
            sx={{ marginLeft: 2 }}
          >
            Daten laden
          </Button>
        </Grid>

        {dateRange[0] && dateRange[1] && (
          <Grid size={12}>
            <Typography variant="h6" gutterBottom>
              Zeitraum: {dateRange[0].format("DD.MM.YYYY")} -{" "}
              {dateRange[1].format("DD.MM.YYYY")}
            </Typography>
          </Grid>
        )}

        <Grid size={12}>
          <Box sx={{ height: "70vh", width: "100%" }}>
            <DataGridPro
              rows={employeeStats}
              columns={columns}
              loading={isLoading}
              localeText={dataGridDEde}
              getRowId={(row) => row.id}
              columnGroupingModel={columnGroupingModel}
              disableVirtualization
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  )
}

export default EmployeeStats2Page
